import styled, {css} from 'styled-components';

import type {
  StyledSecondLevelMenuProps,
  StyledListItemProps,
  StyledSideMenuVariantProps,
  StyledSideMenuProps,
} from './typings';

import {StyledProfileMenuContainer} from '@/molecules/Fragments/Menu/Profile/styles';
import {StyledLogoContainer} from '@/atoms/Logo/styles';
import {StyledSearch} from '@/molecules/Search/styles';
import breakpoints from '@/theme/constants/breakpoints';
import scrollbar from '@/theme/elements/scrollbar';
import zIndex from '@/theme/constants/zIndex';
import colors from '@/theme/constants/colors';
import typography from '@/theme/typography';
import {sizePx} from '@/theme/helpers/size';
import focus from '@/theme/focus';

const navigationHeight = 74;
const sideMenuWidth = 320;

const sharedNavigationBtnStyling = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-color: transparent;
  color: inherit;
  ${focus.brand('inset')}
  font-weight: bold;
  padding: 0;

  &:hover,
  &:active,
  &:focus-visible {
    color: ${colors.darkBlue100};
    background: transparent;
    border-color: transparent;
  }
`;

const liHas = (item: string) => {
  switch (item) {
    case 'logo':
      return css`
        position: absolute;
        left: 0;
        bottom: 0;
        top: 0;
        right: 0;
        margin: auto;
        width: fit-content;
      `;
    case 'search':
      return css`
        position: absolute;
        left: 32px;
        bottom: 0;
        top: 0;
        margin: 0;
        padding-left: 8px;
      `;
    case 'menuIcon':
      return css`
        margin-right: auto;
      `;
    default:
      break;
  }
};

export const StyledMenuIconButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  padding: ${sizePx(6)};
`;

export const StyledListItem = styled.li<StyledListItemProps>`
  ${typography.Body1}
  &&& {
    ${(props) => props.$has && liHas(props.$has)}
  }
  ${StyledLogoContainer} {
    @media only screen and ${breakpoints.device.m} {
      width: 146px;
      height: 26px;
    }
  }
`;

export const StyledMobileTopNavigation = styled.nav`
  height: ${navigationHeight}px;
  background: ${colors.grey25};
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  z-index: ${zIndex.fixed};

  @media only screen and ${breakpoints.device.s} {
    position: sticky;
    top: 0;
  }

  @media only screen and ${breakpoints.device.l} {
    display: none;
  }

  ${StyledSearch} {
    z-index: 1;
  }
`;

export const StyledUserButtonsList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${sizePx(1)};

  margin: auto 0;
  padding-right: ${sizePx(2)};
`;

export const StyledUserButtonsListItem = styled.li<{$paddingTop?: number}>`
  ${(props) => props.$paddingTop && `padding-top: ${props.$paddingTop}px;`}
  display: flex;
  align-items: center;
  justify-content: center;

  & a {
    padding: ${sizePx(6)};
    &: hover {
      color: ${colors.red100};
    }
    ${focus.brand('inset')}
  }
`;

export const StyledTopNavigationList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;

  > ${StyledListItem} {
    padding: 0;
    margin: 0 ${sizePx(5)};
    line-height: ${navigationHeight}px;
    display: flex;
    align-items: center;
    height: ${navigationHeight}px;
    ${typography.Body1}

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }

    > span,
    > button,
    > a {
      cursor: pointer;
      text-decoration: none;
      color: ${colors.darkBlue100};
      display: flex;
      align-items: center;
      height: 100%;
      ${focus.brand('inset')}

      // TODO: dashboard icon has bigger width and aligned to the top, so need this to align all icons evenly.
      .icon-dashboard {
        margin-top: 3px;
        margin-right: 1px;
      }

      &:hover {
        color: ${colors.red100};
      }

      > ${StyledProfileMenuContainer} {
        height: 100%;
      }

      > .bi-logo {
        color: ${colors.red100};
      }
    }
  }
`;

export const StyledDrawerBackdrop = styled.div<StyledSideMenuProps>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.modalBackdrop};
  background: rgba(0, 0, 0, 0.3);
  cursor: default;
`;

export const StyledSideMenuListItem = styled.li<StyledSideMenuVariantProps>`
  ${(props) =>
    props.$isSubMenu
      ? css`
          ${typography.Body1}
        `
      : css`
          ${typography.Subtitle1}
        `}
  > a, > button {
    ${focus.brand()}
    ${(props) => !props.$isSubMenu && '&:focus-visible { border-color: unset;}'}
  }
`;

export const StyledSideMenuButton = styled.button`
  ${typography.Subtitle1}
  background: none;
  border: none;
  margin: 0;
  cursor: pointer;
  text-decoration: none;
  padding: 0 2px 0 0;
  flex-grow: 1;

  display: flex;
  justify-content: space-between;
  align-items: center;

  color: inherit;
  line-height: inherit;
  &:hover {
    color: ${colors.red100};
  }
  &::after {
    content: '';
    display: block;
    width: ${sizePx(5)};
    height: ${sizePx(5)};
    border-top: 3px solid;
    border-right: 3px solid;
    border-color: inherit;
    transform: rotate(45deg);
  }
`;

export const StyledSideMenuDrawer = styled.div<StyledSideMenuProps>`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  width: ${sideMenuWidth}px;
  position: fixed;
  z-index: ${zIndex.modal}; // Chose this zIndex as it will have a backdrop and needs to be on top of navigation that may also be fixed
  transition: all 200ms ease-in;
  top: 0;
  bottom: 0;
  left: -${sideMenuWidth}px;
  overflow: hidden;
  ${(props) => props.$isOpen && `transform: translate3d(${sideMenuWidth}px, 0, 0);`}
`;

export const StyledFirstLevelMenu = styled.nav`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  ${scrollbar.primary}

  padding: 0 ${sizePx(8)} ${sizePx(20)} ${sizePx(8)};
`;

export const StyledSideMenuListFigure = styled.figure<{$isTopLevel?: boolean}>`
  margin: 0;

  > figcaption {
    ${(props) =>
      props.$isTopLevel !== true &&
      css`
        ${typography.Subtitle1}
        padding-top: ${sizePx(6)};
        padding-bottom: ${sizePx(6)};
        padding-left: ${sizePx(8)};
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 50px;
      `}
  }
`;

export const StyledSideMenuList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  width: 100%;

  ${StyledSideMenuListItem} {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding-top: ${sizePx(6)};
    padding-bottom: ${sizePx(6)};

    > span,
    > a {
      display: block;
      flex-grow: 1;
      text-align: left;
      color: inherit;
      line-height: inherit;

      &:first-letter {
        text-transform: uppercase;
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.grey100};
    }

    &:hover {
      color: ${colors.red100};
    }
  }
`;

export const StyledCloseButton = styled.button`
  ${sharedNavigationBtnStyling}

  > span {
    width: ${sizePx(12)};
    height: ${sizePx(12)};
    transform: rotate(45deg);
  }
`;

export const StyledGoBackButton = styled.button`
  ${sharedNavigationBtnStyling}

  > span {
    width: ${sizePx(12)};
    height: ${sizePx(12)};
    margin-top: ${sizePx(2)};
  }
`;

export const StyledSideMenuControls = styled.div<StyledSideMenuVariantProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: ${sizePx(28)};

  ${StyledCloseButton} {
    margin-left: auto;
  }
`;

export const StyledSecondLevelMenu = styled.nav<StyledSecondLevelMenuProps>`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  ${(props) => css`
    transition: all ${props.$animationDuration}ms ease-in;
  `}
  width:${sideMenuWidth}px;
  height: 100%;
  right: -${sideMenuWidth}px;
  position: absolute;
  padding: 0 ${sizePx(8)} ${sizePx(20)} ${sizePx(8)};
  overflow-y: auto;
  ${(props) => props.$isOpen && `transform: translate3d(-${sideMenuWidth}px, 0, 0);`}
`;

export const StyledSecondLevelContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledUserSigninOptions = styled.ul`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: ${sizePx(12)};
  padding: 0;

  > li {
    min-width: 136px;
  }

  > li > button {
    width: 100%;
  }
`;

export const StyledNavigationButton = styled.button`
  ${typography.Body1}
  background: none;
  border: none;
  margin: 0;
  cursor: pointer;
  text-decoration: none;
  padding: 0;
  flex-grow: 1;
  width: 100%;
  text-align: left;
  color: inherit;
`;
